import {
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  RestaurantMenu as RestaurantMenuIcon,
  Science as ScienceIcon,
  Category as CategoryIcon,
  Inventory2 as Inventory2Icon,
  CompareArrows as CompareArrowsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Assessment as AssessmentIcon,
  Map as MapIcon,
  Payment as PaymentIcon,
  LocalOffer as LocalOfferIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon,
  Loyalty as LoyaltyIcon,
  Redeem as RedeemIcon,
  LocalShipping as LocalShippingIcon,
  DeliveryDining as DeliveryDiningIcon,
  LocationCity as LocationCityIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import type { CompanyPlan } from '../services/companyConfigService';

type MenuRole = string;

export interface MenuItemConfig {
  text: string;
  path: string;
  icon: React.ElementType;
  roles?: MenuRole[];
  minPlan?: CompanyPlan;
}

export interface MenuSectionConfig {
  id: string;
  title: string;
  icon: React.ElementType;
  defaultOpen?: boolean;
  items: MenuItemConfig[];
  roles?: MenuRole[];
  minPlan?: CompanyPlan;
}

export const BASE_SECTIONS: MenuSectionConfig[] = [
  {
    id: 'operations',
    title: 'Operações',
    icon: DashboardIcon,
    defaultOpen: true,
    items: [
      { text: 'Dashboard', path: '/dashboard', icon: DashboardIcon },
      { text: 'Pedidos', path: '/pedidos', icon: ReceiptIcon },
      { text: 'Pedido Avulso', path: '/pedidos-avulsos', icon: ReceiptIcon },
    ],
  },
  {
    id: 'catalog',
    title: 'Cardápio',
    icon: RestaurantMenuIcon,
    defaultOpen: true,
    items: [
      { text: 'Itens', path: '/items', icon: RestaurantMenuIcon },
      {
        text: 'Fichas técnicas',
        path: '/recipes',
        icon: ScienceIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
      {
        text: 'Combos',
        path: '/combos',
        icon: CategoryIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
    ],
  },
  {
    id: 'inventory',
    title: 'Estoque',
    icon: Inventory2Icon,
    items: [
      { text: 'Itens de Estoque', path: '/stock/items', icon: Inventory2Icon },
      {
        text: 'Movimentações',
        path: '/stock/movements',
        icon: CompareArrowsIcon,
      },
      {
        text: 'Saldos',
        path: '/stock/balances',
        icon: AccountBalanceWalletIcon,
      },
    ],
    roles: ['ADMIN', 'DEVELOPER'],
    minPlan: 'FULL_CONTROL_ENTERPRISE',
  },
  {
    id: 'planning',
    title: 'Planejamento',
    icon: AssessmentIcon,
    items: [{ text: 'Projeções', path: '/projecoes', icon: AssessmentIcon }],
    roles: ['ADMIN', 'DEVELOPER'],
    minPlan: 'FULL_CONTROL_ENTERPRISE',
  },
  {
    id: 'reports',
    title: 'Relatórios',
    icon: MapIcon,
    items: [
      {
        text: 'Entregas por bairro',
        path: '/reports/deliveries-neighborhoods',
        icon: MapIcon,
        minPlan: 'GESTAO_PLUS',
      },
      { text: 'Vendas', path: '/reports/sales', icon: AssessmentIcon },
      {
        text: 'Pagamentos',
        path: '/reports/payments',
        icon: PaymentIcon,
        minPlan: 'GESTAO_PLUS',
      },
      {
        text: 'Estoque',
        path: '/reports/stock',
        icon: Inventory2Icon,
        minPlan: 'FULL_CONTROL_ENTERPRISE',
      },
    ],
    roles: ['ADMIN', 'DEVELOPER'],
  },
  {
    id: 'engagement',
    title: 'Engajamento',
    icon: CampaignIcon,
    items: [
      { text: 'Mídias', path: '/media', icon: CampaignIcon },
      { text: 'Cupons', path: '/coupons', icon: LocalOfferIcon },
      { text: 'WhatsApp', path: '/whatsapp', icon: WhatsAppIcon },
      { text: 'Leads', path: '/crm/leads', icon: PeopleIcon },
    ],
    roles: ['ADMIN', 'DEVELOPER'],
    minPlan: 'GESTAO_PLUS',
  },
  {
    id: 'loyalty',
    title: 'Fidelidade',
    icon: LoyaltyIcon,
    items: [
      {
        text: 'Campanhas',
        path: '/loyalty/campaigns',
        icon: CampaignIcon,
        roles: ['ADMIN', 'DEVELOPER'],
        minPlan: 'WHITE_LABEL_BUSINESS',
      },
      {
        text: 'Clientes fidelidade',
        path: '/loyalty/customers',
        icon: PeopleIcon,
        roles: ['ADMIN', 'DEVELOPER'],
        minPlan: 'WHITE_LABEL_BUSINESS',
      },
      {
        text: 'Recompensas',
        path: '/loyalty/rewards',
        icon: RedeemIcon,
        roles: ['ADMIN', 'DEVELOPER'],
        minPlan: 'WHITE_LABEL_BUSINESS',
      },
    ],
    roles: ['ADMIN', 'DEVELOPER'],
    minPlan: 'WHITE_LABEL_BUSINESS',
  },
  {
    id: 'delivery',
    title: 'Entregas',
    icon: LocalShippingIcon,
    items: [
      {
        text: 'Visão geral',
        path: '/delivery',
        icon: LocalShippingIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
      {
        text: 'Entregadores',
        path: '/delivery/couriers',
        icon: DeliveryDiningIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
      {
        text: 'Cidades',
        path: '/delivery/cities',
        icon: LocationCityIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
      {
        text: 'Bairros e taxas',
        path: '/delivery/neighborhoods',
        icon: MapIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
    ],
    roles: ['ADMIN', 'DEVELOPER'],
  },
  {
    id: 'integrations',
    title: 'Integrações',
    icon: SettingsIcon,
    items: [
      {
        text: 'Mercado Pago',
        path: '/admin/integracoes/mercadopago',
        icon: PaymentIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
    ],
    roles: ['ADMIN', 'DEVELOPER'],
  },
  {
    id: 'management',
    title: 'Administração',
    icon: PeopleIcon,
    items: [
      {
        text: 'Usuários',
        path: '/users',
        icon: PeopleIcon,
        roles: ['ADMIN', 'DEVELOPER'],
      },
      {
        text: 'Empresa',
        path: '/company',
        icon: BusinessIcon,
        roles: ['DEVELOPER'],
      },
      {
        text: 'Empresas cadastradas',
        path: '/admin/companies',
        icon: BusinessIcon,
        roles: ['DEVELOPER'],
      },
      {
        text: 'Nova empresa',
        path: '/company/new',
        icon: BusinessIcon,
        roles: ['DEVELOPER'],
      },
      {
        text: 'Usuários (Master)',
        path: '/dev-users',
        icon: AdminPanelSettingsIcon,
        roles: ['DEVELOPER'],
      },
    ],
    roles: ['ADMIN', 'DEVELOPER'],
  },
];
